import { Select } from '@design-system';
import React from 'react';
import styles from './Paginator.module.scss';
import arrowLeftIcon from '@icons/left-arrow.svg';
import arrowRightIcon from '@icons/right-arrow.svg';
import { IOption } from '@ds/elements/Select/Select.types';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';
import { CaretDropdownIcon } from '@ds-icons';
import classNames from 'classnames';

interface PaginatorProps {
  totalRows: number;
  rowsPerPage?: number;
  selectedPage: number;
  dropdownOptions: { label: string; value: number }[];
  onSelectRowsPerPageDropdown: (opt: IOption) => void;
  onChangePageInput: (opt: IOption | null) => void;
  entriesPerRow?: number;
}

const Paginator: React.FC<PaginatorProps> = ({
  totalRows,
  rowsPerPage = 10,
  dropdownOptions,
  selectedPage,
  onSelectRowsPerPageDropdown,
  onChangePageInput,
  entriesPerRow = 1,
}) => {
  const displayMessage = totalRows
    ? `Displaying: ${Math.min(rowsPerPage * selectedPage * entriesPerRow, totalRows)} of ${totalRows}`
    : '';

  const maxPage = Math.ceil(totalRows / (rowsPerPage * entriesPerRow));

  const pageOptions = Array.from({ length: maxPage }, (_, i) => ({
    label: (i + 1).toString(),
    value: i + 1,
  }));

  const isMobile = useScreenSize().isMobile;

  return (
    <div className={styles.paginator}>
      <div className={styles['page-selector']}>
        <Select
          placeholder="-"
          options={dropdownOptions}
          selectedOption={{ label: `${rowsPerPage} rows`, value: rowsPerPage }}
          onChange={(opt) => {
            if (opt) {
              onSelectRowsPerPageDropdown(opt);
              onChangePageInput({ value: 1, label: '1' });
            }
          }}
          highlightBorder={false}
          icon={CaretDropdownIcon}
          dropdownWrapperClassName={styles['paginator-dropdown-element-wrapper']}
        />
        <p className={styles['per-page-text']}>per page</p>
      </div>
      {!isMobile && <p className={styles['page-counter']}>{displayMessage}</p>}
      <div className={styles['page-selector']}>
        <div className={styles['page-link']}>
          <img
            src={arrowLeftIcon}
            className={selectedPage === 1 ? styles.disabled : ''}
            onClick={() => {
              if (selectedPage !== 1) {
                onChangePageInput({ value: selectedPage - 1, label: (selectedPage - 1).toString() });
              }
            }}
          />
        </div>

        <div className={styles['page-selector']}>
          <p>Go to page</p>
          <Select
            placeholder="-"
            options={pageOptions}
            selectedOption={{ label: selectedPage.toString(), value: selectedPage }}
            onChange={onChangePageInput}
            highlightBorder={false}
            icon={CaretDropdownIcon}
            dropdownWrapperClassName={styles['paginator-dropdown-element-wrapper']}
          />
        </div>

        <div className={classNames(styles['page-link'], styles['page-link-right'])}>
          <img
            src={arrowRightIcon}
            className={selectedPage === maxPage ? styles.disabled : ''}
            onClick={() => {
              if (selectedPage !== maxPage) {
                onChangePageInput({ value: selectedPage + 1, label: (selectedPage + 1).toString() });
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Paginator;
